import { forwardRef } from 'react';
import { getTeamDisplay } from './MiracleTeamPicker';
import { TeamLabel } from './TeamLabel';
import { cn } from '../utils/cn';
import {
  STAT_CARD_EXPORT_PX,
  STAT_CARD_SHARE_WIDTH,
  STAT_CARD_SHARE_HEIGHT,
} from '../utils/memeExport';
import { EXPORT_WATERMARK_TEXT } from '../constants/branding';

function pct(x) {
  if (x >= 0.1) return `${(x * 100).toFixed(0)}%`;
  if (x >= 0.01) return `${(x * 100).toFixed(1)}%`;
  return `${(x * 100).toFixed(2)}%`;
}

const CARD_COPY = {
  es: {
    trailing: 'el que va perdiendo',
    leading: 'el que va ganando',
    minute: 'Min',
    downBy: 'abajo por',
    goals: 'goles',
    goal: 'gol',
    winChance: 'chance de ganar',
    tool: 'La Calculadora Milagrosa',
  },
  en: {
    trailing: 'the trailing team',
    leading: 'the leading team',
    minute: 'Min',
    downBy: 'down by',
    goals: 'goals',
    goal: 'goal',
    winChance: 'win chance',
    tool: 'The Miracle Calculator',
  },
};

const MiracleStatCard = forwardRef(function MiracleStatCard(
  { teamA, teamB, diff, minute, probs, lang, variant = 'display' },
  ref
) {
  const c = CARD_COPY[lang] || CARD_COPY.en;
  const trailName = getTeamDisplay(teamA);
  const leadName = getTeamDisplay(teamB);
  const goalWord = diff === 1 ? c.goal : c.goals;
  const isExport = variant === 'export';
  const isShare = variant === 'share';

  if (isShare) {
    return (
      <div
        ref={ref}
        className="bg-slate-950 border border-slate-800 rounded-xl flex flex-col overflow-hidden shrink-0 shadow-2xl"
        style={{
          fontFamily: 'system-ui, sans-serif',
          width: STAT_CARD_SHARE_WIDTH,
          height: STAT_CARD_SHARE_HEIGHT,
        }}
      >
        <div className="bg-gradient-to-b from-slate-800 to-slate-950 text-center border-b border-slate-800 px-10 pt-5 pb-3">
          <p className="font-black uppercase tracking-[0.15em] text-lime-400/80 text-sm">
            {c.tool}
          </p>
          <div className="flex items-center justify-center gap-5 mt-2 font-black text-white text-3xl">
            {teamA.mode !== 'none' && teamA.name ? (
              <TeamLabel
                team={teamA}
                flagSize="xl"
                className="inline-flex items-center max-w-[38%]"
              />
            ) : (
              <span className="truncate text-slate-400 max-w-[38%]">{c.trailing}</span>
            )}
            <span className="text-slate-500 font-bold shrink-0 text-xl">vs</span>
            {teamB.mode !== 'none' && teamB.name ? (
              <TeamLabel
                team={teamB}
                flagSize="xl"
                className="inline-flex items-center max-w-[38%]"
              />
            ) : (
              <span className="truncate text-slate-400 max-w-[38%]">{c.leading}</span>
            )}
          </div>
          <p className="text-slate-400 font-medium mt-2 text-lg">
            {trailName ? (
              <TeamLabel team={teamA} flagSize="md" className="inline-flex items-center" />
            ) : (
              c.trailing
            )}{' '}
            <span className="text-slate-300">
              {c.downBy} {diff} {goalWord}
            </span>{' '}
            · {c.minute} {minute}&apos;
          </p>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-10 py-4 min-h-0">
          <p className="font-black uppercase tracking-widest text-lime-400 text-center text-sm mb-2">
            {trailName ? (
              <>
                <TeamLabel team={teamA} flagSize="lg" className="inline-flex items-center" /> —{' '}
                {c.winChance}
              </>
            ) : (
              c.winChance
            )}
          </p>
          <p
            className="font-black italic leading-none text-lime-400 tracking-tight"
            style={{ fontSize: '5.5rem' }}
          >
            {pct(probs.win)}
          </p>
          <div className="flex gap-12 mt-4 text-lg">
            <div className="text-center">
              <span className="text-sky-400 font-extrabold block">{pct(probs.draw)}</span>
              <span className="text-slate-500 uppercase font-bold tracking-wider text-xs">
                {lang === 'es' ? 'Empate' : 'Draw'}
              </span>
            </div>
            <div className="text-center">
              <span className="text-emerald-400 font-extrabold block">{pct(probs.lose)}</span>
              <span className="text-slate-500 uppercase font-bold tracking-wider text-xs">
                {leadName ? (
                  <TeamLabel
                    team={teamB}
                    flagSize="md"
                    className="inline-flex items-center justify-center"
                  />
                ) : lang === 'es' ? (
                  'Gana rival'
                ) : (
                  'Rival wins'
                )}
              </span>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-800 flex justify-between items-center bg-slate-900/80 px-10 py-3">
          <span className="font-bold uppercase tracking-wider text-slate-500 text-sm">
            {EXPORT_WATERMARK_TEXT}
          </span>
          <span className="font-black uppercase tracking-wider text-lime-400/70 italic text-sm">
            {lang === 'es' ? 'Mentalidad Ganadora' : 'Winning Mindset'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={cn(
        'bg-slate-950 border border-slate-800 rounded-2xl flex flex-col shadow-2xl',
        isExport
          ? 'overflow-hidden shrink-0'
          : 'aspect-square w-full max-w-md mx-auto overflow-hidden'
      )}
      style={{
        fontFamily: 'system-ui, sans-serif',
        ...(isExport
          ? { width: STAT_CARD_EXPORT_PX, height: STAT_CARD_EXPORT_PX }
          : {}),
      }}
    >
      <div
        className={cn(
          'bg-gradient-to-b from-slate-800 to-slate-950 text-center border-b border-slate-800',
          isExport ? 'px-12 pt-12 pb-6' : 'px-5 pt-5 pb-3'
        )}
      >
        <p
          className={cn(
            'font-black uppercase tracking-[0.2em] text-lime-400/80',
            isExport ? 'text-xl mb-4' : 'text-[10px] mb-3'
          )}
        >
          {c.tool}
        </p>
        <div
          className={cn(
            'flex items-center justify-center font-black text-white',
            isExport ? 'gap-6 text-4xl' : 'gap-3 text-sm sm:text-base'
          )}
        >
          {teamA.mode !== 'none' && teamA.name ? (
            <TeamLabel
              team={teamA}
              flagSize={isExport ? 'xxl' : 'xl'}
              className={cn(
                'inline-flex items-center',
                isExport ? 'max-w-[40%]' : 'max-w-[38%]'
              )}
            />
          ) : (
            <span className={cn('truncate text-slate-400', isExport ? 'max-w-[40%]' : 'max-w-[38%]')}>
              {c.trailing}
            </span>
          )}
          <span className={cn('text-slate-500 font-bold shrink-0', isExport ? 'text-2xl' : 'text-xs')}>
            vs
          </span>
          {teamB.mode !== 'none' && teamB.name ? (
            <TeamLabel
              team={teamB}
              flagSize={isExport ? 'xxl' : 'xl'}
              className={cn(
                'inline-flex items-center',
                isExport ? 'max-w-[40%]' : 'max-w-[38%]'
              )}
            />
          ) : (
            <span className={cn('truncate text-slate-400', isExport ? 'max-w-[40%]' : 'max-w-[38%]')}>
              {c.leading}
            </span>
          )}
        </div>
        <p
          className={cn(
            'text-slate-400 font-medium',
            isExport ? 'mt-6 text-2xl' : 'mt-3 text-xs'
          )}
        >
          {trailName ? (
            <TeamLabel team={teamA} flagSize={isExport ? 'lg' : 'md'} className="inline-flex items-center" />
          ) : (
            c.trailing
          )}{' '}
          <span className="text-slate-300">
            {c.downBy} {diff} {goalWord}
          </span>{' '}
          · {c.minute} {minute}&apos;
        </p>
      </div>

      <div
        className={cn(
          'flex-1 flex flex-col items-center justify-center',
          isExport ? 'px-12 py-10' : 'px-5 py-6'
        )}
      >
        <p
          className={cn(
            'font-black uppercase tracking-widest text-lime-400 text-center',
            isExport ? 'text-xl mb-4' : 'text-[10px] mb-2'
          )}
        >
          {trailName ? (
            <>
              <TeamLabel
                team={teamA}
                flagSize={isExport ? 'xl' : 'lg'}
                className="inline-flex items-center"
              />{' '}
              — {c.winChance}
            </>
          ) : (
            c.winChance
          )}
        </p>
        <p
          className="font-black italic leading-none text-lime-400 tracking-tight"
          style={{ fontSize: isExport ? '8.5rem' : 'clamp(3.5rem, 18vw, 5.5rem)' }}
        >
          {pct(probs.win)}
        </p>
        <div className={cn('flex gap-6', isExport ? 'mt-10 text-xl' : 'mt-5 text-xs')}>
          <div className="text-center">
            <span className="text-sky-400 font-extrabold block">{pct(probs.draw)}</span>
            <span
              className={cn(
                'text-slate-500 uppercase font-bold tracking-wider',
                isExport ? 'text-sm' : 'text-[9px]'
              )}
            >
              {lang === 'es' ? 'Empate' : 'Draw'}
            </span>
          </div>
          <div className="text-center">
            <span className="text-emerald-400 font-extrabold block">{pct(probs.lose)}</span>
            <span
              className={cn(
                'text-slate-500 uppercase font-bold tracking-wider',
                isExport ? 'text-sm' : 'text-[9px]'
              )}
            >
              {leadName ? (
                <TeamLabel
                  team={teamB}
                  flagSize={isExport ? 'lg' : 'md'}
                  className="inline-flex items-center justify-center"
                />
              ) : lang === 'es' ? (
                'Gana rival'
              ) : (
                'Rival wins'
              )}
            </span>
          </div>
        </div>
      </div>

      <div
        className={cn(
          'border-t border-slate-800 flex justify-between items-center bg-slate-900/80',
          isExport ? 'px-12 py-6' : 'px-5 py-3'
        )}
      >
        <span
          className={cn(
            'font-bold uppercase tracking-wider text-slate-500',
            isExport ? 'text-lg' : 'text-[10px]'
          )}
        >
          {EXPORT_WATERMARK_TEXT}
        </span>
        <span
          className={cn(
            'font-black uppercase tracking-wider text-lime-400/70 italic',
            isExport ? 'text-lg' : 'text-[10px]'
          )}
        >
          {lang === 'es' ? 'Mentalidad Ganadora' : 'Winning Mindset'}
        </span>
      </div>
    </div>
  );
});

export default MiracleStatCard;
